import { Button, DialogContent } from '@mui/material';
import {
    DialogActions,
    RoutedDialog,
    RoutedDialogImplProps,
    TextField,
    replaceItemById,
    useForm,
    usePageMessage,
    useParams,
    renderEnumOptions
} from '@tsp-ui/core';
import { useAsyncEffect } from '@tsp-ui/core/utils';
import { useCallback, useContext, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import {
    Instance, createInstance, fetchInstances, updateInstance
} from '../../api';

import styles from './InstanceManagementDialog.module.scss';
import { OrgMgmtPageContext } from './OrganizationManagementPage';


interface OrganizationMgmtParams {
    orgId: string;
    instanceId?: string;
}

enum InstanceType {
    PROD = 'PROD',
    TEST = 'TEST',
    UAT = 'UAT'
}

const instanceTypeDisplay = {
    [InstanceType.PROD]: 'Production',
    [InstanceType.TEST]: 'R2T',
    [InstanceType.UAT]: 'UAT'
};

interface InstanceFormValues extends Instance {
    instanceType: InstanceType;
}

export default function InstanceManagementDialog({ ...props }: RoutedDialogImplProps) {
    const pageMessage = usePageMessage();
    const navigate = useNavigate();

    const { orgId, instanceId } = useParams<OrganizationMgmtParams>();
    const { setInstanceTableKey, organizations, setOrganizations } = useContext(OrgMgmtPageContext);

    const [ instances, setInstances ] = useState<Instance[]>([]);
    const [ loading, setLoading ] = useState(false);
    const [ saveLoading, setSaveLoading ] = useState(false);

    const formMethods = useForm<InstanceFormValues>();
    const { reset } = formMethods;

    useAsyncEffect(useCallback(async () => {
        if (instanceId) {
            setLoading(true);

            try {
                const instances = await fetchInstances(orgId);
                setInstances(instances);

                const instance = instances.find(inst => inst.id === instanceId);

                reset(!instance ? undefined : {
                    ...instance,
                    instanceType: instance.isUat
                        ? InstanceType.UAT
                        : instance.isTest
                            ? InstanceType.TEST
                            : InstanceType.PROD
                });
            } catch (error) {
                pageMessage.handleApiError('An error occurred while fetching instances', error);
            }

            setLoading(false);
        }
    }, [
        pageMessage, orgId, reset, instanceId
    ]));

    const handleSubmit = formMethods.handleSubmit(async (formValues) => {
        const org = organizations.find(({ id }) => id === orgId);
        if (!org) {
            pageMessage.error(`Organization with Org ID: ${orgId} not found`);
            return;
        }

        setSaveLoading(true);

        if (formValues.instanceType === InstanceType.PROD) {
            formValues.isUat = false;
            formValues.isTest = false;
        } else if(formValues.instanceType === InstanceType.TEST) {
            formValues.isUat = false;
            formValues.isTest = true;
        } else if(formValues.instanceType === InstanceType.UAT) {
            formValues.isUat = true;
            formValues.isTest = true;
        }

        try {
            if (instanceId) {
                setInstances(replaceItemById(
                    instances,
                    await updateInstance(orgId, formValues)
                ));
            } else {
                setInstances([
                    ...instances,
                    await createInstance(orgId, formValues)
                ]);
            }

            setOrganizations(replaceItemById(organizations, {
                ...org,
                numInstances: org.numInstances + 1
            }));

            if (org.numInstances > 0) {
                setInstanceTableKey(key => key + 1);
            }

            navigate('..');

            pageMessage.success(`Instance ${instanceId ? 'updated' : 'created'}`);
        } catch (error) {
            pageMessage.handleApiError(
                `An error occurred while ${instanceId ? 'updating' : 'creating'} the instance`, error
            );
        }

        setSaveLoading(false);
    });

    return (
        <RoutedDialog
            title={`${instanceId ? 'Update' : 'Add'} instance`}
            loading={loading}
            {...props}
        >
            <DialogContent>
                <form
                    id={formId}
                    onSubmit={handleSubmit}
                    className={styles.form}
                    noValidate
                >
                    <FormProvider {...formMethods}>
                        <TextField<InstanceFormValues>
                            name="instanceId"
                            label="Instance ID"
                            required
                            rules={{
                                validate: (value) => (
                                    instances.some(inst => inst.id !== instanceId && inst.instanceId === value)
                                        ? 'Instance ID must be unique' : undefined
                                )
                            }}
                        />

                        <TextField<InstanceFormValues>
                            name="clientId"
                            label="Client ID"
                            required
                        />

                        <TextField<InstanceFormValues>
                            name="clientSecret"
                            label="Secret"
                            required
                            multiline
                            className={styles.secret}
                        />

                        <TextField<InstanceFormValues>
                            name="instanceType"
                            label="Instance Type"
                            required
                            select
                        >
                            {renderEnumOptions(instanceTypeDisplay)}
                        </TextField>
                    </FormProvider>
                </form>
            </DialogContent>

            <DialogActions loading={saveLoading}>
                <Button
                    type="submit"
                    variant="contained"
                    color="secondary"
                    form={formId}
                    disabled={saveLoading}
                >
                    Save
                </Button>
            </DialogActions>
        </RoutedDialog>
    );
}

const formId = 'inst-management-form';
